import * as React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Row } from "components/bootstrap";
import {
  useInputSetupWizard,
  ButtonCol,
  StepWrapper,
  useInputSetupWizardSteps,
  useInputSetupWizardStepsHelper,
  INPUT_WIZARD_STEPS,
  INPUT_WIZARD_FLOWS,
  OPEN_FLOW_STEPS,
  StyledHeading
} from "components/inputs/InputSetupWizard";
import EntityCreateShareFormGroup from "components/permissions/EntityCreateShareFormGroup";
import SelectSpotlightPacks from "./components/SelectSpotlightPacks";
import SelectProcessingPacks from "./components/SelectProcessingPacks";
import useFilterIlluminatePacks from "../hooks/useFilterIlluminatePacks";
export const ILLUMINATE_PACK_TYPES = {
  PROCESSING: "PROCESSING",
  SPOTLIGHT: "SPOTLIGHT",
  SHARE: "SHARE"
};
const SelectIlluminateStep = () => {
  var _a;
  const { t } = useTranslation("enterprise_forwarder");
  const currentStepName = INPUT_WIZARD_STEPS.SELECT_ILLUMINATE;
  const [shownSection, setShownSection] = useState(ILLUMINATE_PACK_TYPES.PROCESSING);
  const [selectedProcessingPacks, setSelectedProcessingPacks] = useState([]);
  const [shareRequest, setShareRequest] = useState(null);
  const [selectedSpotlightPacks, setSelectedSpotlightPacks] = useState([]);
  const [touchedProcessingPacks, setTouchedProcessingPacks] = useState(false);
  const [touchedSpotlightPacks, setTouchedSpotlightPacks] = useState(false);
  const {
    goToPreviousStep,
    goToNextStep,
    orderedSteps,
    setOrderedSteps,
    activeStep,
    setActiveStep,
    wizardData,
    setWizardData
  } = useInputSetupWizard();
  const { data: processingPacksData, isLoading: isLoadingProcessingPacks } = useFilterIlluminatePacks({
    type: ILLUMINATE_PACK_TYPES.PROCESSING,
    inputType: (_a = wizardData.input) == null ? void 0 : _a.type
  });
  const { data: spotlightPacksData, isLoading: isLoadingSpotlightPacksData } = useFilterIlluminatePacks(
    {
      type: ILLUMINATE_PACK_TYPES.SPOTLIGHT
    },
    shownSection === ILLUMINATE_PACK_TYPES.PROCESSING
  );
  const { stepsData, setStepsData } = useInputSetupWizardSteps();
  const { checkHasNextStep, checkHasPreviousStep, updateStepData, getStepData } = useInputSetupWizardStepsHelper();
  const hasPreviousStep = checkHasPreviousStep(orderedSteps, activeStep);
  const hasNextStep = checkHasNextStep(orderedSteps, activeStep);
  const enabledProcessingPacks = processingPacksData == null ? void 0 : processingPacksData.filter((pack) => pack.enabled).map((pack) => pack.pack_id);
  const determineSelectedProcessingPacks = useCallback(() => {
    if (touchedProcessingPacks) {
      return selectedProcessingPacks;
    }
    const previouslySelectedPacks = getStepData(stepsData, currentStepName, "selectedIlluminatePacks");
    if ((previouslySelectedPacks == null ? void 0 : previouslySelectedPacks.length) > 0) {
      return previouslySelectedPacks;
    }
    return [];
  }, [currentStepName, selectedProcessingPacks, stepsData, touchedProcessingPacks, getStepData]);
  const availableSpotlightPacks = useCallback(
    () => spotlightPacksData.filter(
      (spotlightPack) => spotlightPack.requirements.every((requiredPackId) => enabledProcessingPacks.includes(requiredPackId)) || spotlightPack.requirements.some(
        (requiredPackId) => determineSelectedProcessingPacks().includes(requiredPackId)
      )
    ),
    [enabledProcessingPacks, spotlightPacksData, determineSelectedProcessingPacks]
  );
  const preselectedSpotlightPacks = useCallback(
    () => determineSelectedProcessingPacks().map(
      (selectedProcessingPackId) => {
        var _a2;
        return (_a2 = availableSpotlightPacks().find(
          (spotlightPack) => spotlightPack.requirements.includes(selectedProcessingPackId)
        )) == null ? void 0 : _a2.pack_id;
      }
    ),
    [determineSelectedProcessingPacks, availableSpotlightPacks]
  );
  const determineSelectedSpotlightPacks = useCallback(() => {
    if (touchedSpotlightPacks) {
      return selectedSpotlightPacks;
    }
    return [...preselectedSpotlightPacks(), ...selectedSpotlightPacks];
  }, [preselectedSpotlightPacks, selectedSpotlightPacks, touchedSpotlightPacks]);
  const onNextStep = () => {
    if (shownSection === ILLUMINATE_PACK_TYPES.PROCESSING) {
      setShownSection(ILLUMINATE_PACK_TYPES.SPOTLIGHT);
    } else if (shownSection === ILLUMINATE_PACK_TYPES.SPOTLIGHT) {
      setShownSection(ILLUMINATE_PACK_TYPES.SHARE);
    } else {
      setStepsData(
        updateStepData(stepsData, currentStepName, {
          selectedIlluminatePacks: determineSelectedProcessingPacks(),
          selectedSpotlightPacks: determineSelectedSpotlightPacks(),
          shareRequest
        })
      );
      goToNextStep();
    }
  };
  const resetSpotlightPacks = () => {
    setTouchedSpotlightPacks(false);
    setSelectedSpotlightPacks([]);
  };
  const onPreviousStep = () => {
    if (shownSection === ILLUMINATE_PACK_TYPES.SPOTLIGHT) {
      resetSpotlightPacks();
      setShownSection(ILLUMINATE_PACK_TYPES.PROCESSING);
    } else if (shownSection === ILLUMINATE_PACK_TYPES.SHARE) {
      setShownSection(ILLUMINATE_PACK_TYPES.SPOTLIGHT);
    } else {
      goToPreviousStep();
    }
  };
  const onSkip = () => {
    setOrderedSteps(OPEN_FLOW_STEPS[INPUT_WIZARD_FLOWS.NON_ILLUMINATE]);
    setActiveStep(OPEN_FLOW_STEPS[INPUT_WIZARD_FLOWS.NON_ILLUMINATE][0]);
    setWizardData({ ...wizardData, flow: INPUT_WIZARD_FLOWS.NON_ILLUMINATE });
  };
  const noProcessingPackSelected = determineSelectedProcessingPacks().length <= 0;
  return /* @__PURE__ */ React.createElement(StepWrapper, null, shownSection === ILLUMINATE_PACK_TYPES.PROCESSING && /* @__PURE__ */ React.createElement(
    SelectProcessingPacks,
    {
      selectedProcessingPacks: determineSelectedProcessingPacks(),
      setSelectedProcessingPacks,
      processingPacksData,
      isLoadingProcessingPacks,
      setTouched: setTouchedProcessingPacks
    }
  ), shownSection === ILLUMINATE_PACK_TYPES.SPOTLIGHT && /* @__PURE__ */ React.createElement(
    SelectSpotlightPacks,
    {
      spotlightPacksData: availableSpotlightPacks(),
      isLoadingSpotlightPacksData,
      selectedSpotlightPacks: determineSelectedSpotlightPacks(),
      setSelectedSpotlightPacks,
      setTouched: setTouchedSpotlightPacks
    }
  ), shownSection === ILLUMINATE_PACK_TYPES.SHARE && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledHeading, null, t("select_illuminate.share_heading")), /* @__PURE__ */ React.createElement(
    EntityCreateShareFormGroup,
    {
      description: t("select_illuminate.share_description"),
      entityType: "illuminate pack",
      entityTitle: "",
      onSetEntityShare: setShareRequest
    }
  )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(ButtonCol, { md: 12 }, (hasPreviousStep || shownSection === ILLUMINATE_PACK_TYPES.SPOTLIGHT || shownSection === ILLUMINATE_PACK_TYPES.SHARE) && /* @__PURE__ */ React.createElement(Button, { onClick: onPreviousStep }, t("select_illuminate.back")), /* @__PURE__ */ React.createElement(Button, { onClick: onSkip }, t("select_illuminate.skip")), hasNextStep && /* @__PURE__ */ React.createElement(Button, { onClick: onNextStep, bsStyle: "primary", disabled: noProcessingPackSelected }, t("select_illuminate.next")))));
};
export default SelectIlluminateStep;
